import { useMemo, useState } from "react";
import { api, useLoad } from "../api";
import { CATEGORY_LABEL, fmt } from "../lib/format";
import { linkHandler } from "../lib/router";
import { KIND_LABEL, MODULE_LABEL, SignalItem } from "./MarketPage";
import { FilteredSignalsToggle, SelectField } from "../components/ui";
import { useShowFiltered } from "../lib/settings";

/** Журнал сигналов всех рынков (ТЗ 3.3): модуль, направление, статус, движение цены через 4 / 13 / 26 недель. */
export function JournalPage() {
  const [days, setDays] = useState(90);
  const [kind, setKind] = useState<string>("all");
  const [module, setModule] = useState<string>("all");
  const [showFiltered] = useShowFiltered();
  const { data, error, loading } = useLoad((s) => api.journal(days, s), [days]);
  // Фильтры типа и модуля сужают выборку; «Показывать отфильтрованные сигналы» только прячет бледные строки.
  const matched = useMemo(
    () =>
      (data ?? []).filter(
        (r) => (kind === "all" || r.signal.kind === kind) && (module === "all" || r.signal.module === module),
      ),
    [data, kind, module],
  );
  const filteredCount = matched.filter((r) => r.signal.filteredByTrend).length;
  const rows = useMemo(() => matched.filter((r) => showFiltered || !r.signal.filteredByTrend), [matched, showFiltered]);
  const modules = useMemo(() => [...new Set((data ?? []).map((r) => r.signal.module))].sort(), [data]);
  // Статистика по модулям: доля сигналов, после которых цена через 13 недель ушла в сторону сигнала.
  const stats = useMemo(() => {
    const m = new Map<string, { n: number; hit: number }>();
    for (const r of data ?? []) {
      const s = r.signal;
      if (s.fwd13 == null || s.direction === 0 || s.filteredByTrend) continue;
      const k = `${s.module}|${s.direction}`;
      const e = m.get(k) ?? { n: 0, hit: 0 };
      e.n++;
      if (Math.sign(s.fwd13) === s.direction) e.hit++;
      m.set(k, e);
    }
    return [...m.entries()].sort();
  }, [data]);

  return (
    <main className="page journal-page">
      <a className="back" href="/ru" onClick={linkHandler("/ru")}>
        ← Все рынки
      </a>
      <h1>Журнал сигналов</h1>
      <p className="lede">
        Все события методики по всем рынкам с параметрами по умолчанию (недельные бары). Отфильтрованные сигналы (против
        тренда или фильтра COT) по методике не берут, но хранят: их можно показать переключателем — они будут бледными.
        Движение цены — к нескорректированной цене дня сигнала.
      </p>
      <div className="filter-bar" role="group" aria-label="Фильтры журнала">
        <SelectField
          label="Период"
          value={days}
          onChange={setDays}
          options={[
            [30, "30 дней"],
            [90, "90 дней"],
            [365, "Год"],
            [3650, "Вся история"],
          ]}
        />
        <SelectField
          label="Тип"
          value={kind}
          onChange={setKind}
          options={[["all", "Все"], ...Object.entries(KIND_LABEL).map(([k, v]) => [k, v.charAt(0).toUpperCase() + v.slice(1)] as [string, string])]}
        />
        <SelectField
          label="Модуль"
          className="select-wide"
          value={module}
          onChange={setModule}
          options={[["all", "Все модули"], ...modules.map((m) => [m, `${m} — ${MODULE_LABEL[m] ?? m}`] as [string, string])]}
        />
        <FilteredSignalsToggle total={filteredCount} />
      </div>
      {error && <div className="notice notice-error">Не удалось загрузить журнал: {error}</div>}
      {loading && !data && <div className="skeleton">Считаем сигналы всех рынков…</div>}
      {stats.length > 0 && (
        <section>
          <h2>Попадания через 13 недель</h2>
          <p className="lede">
            Доля активных сигналов, после которых цена через 13 недель ушла в их сторону (ТЗ M5: статистика отдельно по
            направлениям). Малые выборки — не вывод, а ориентир.
          </p>
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th className="text">Модуль</th>
                  <th className="text">Направление</th>
                  <th>Сигналов</th>
                  <th>Попаданий</th>
                </tr>
              </thead>
              <tbody>
                {stats.map(([k, v]) => {
                  const [m, dir] = k.split("|");
                  return (
                    <tr key={k}>
                      <td className="text">
                        {m} — {MODULE_LABEL[m] ?? m}
                      </td>
                      <td className="text">{Number(dir) > 0 ? "покупка" : "продажа"}</td>
                      <td>{v.n}</td>
                      <td>{fmt.pct((v.hit / v.n) * 100)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}
      <section className="journal">
        <h2>
          События <span className="h-count">{rows.length}</span>
        </h2>
        {data && rows.length === 0 && (
          <p className="empty-state">
            {matched.length > 0 ? "Все события по этим фильтрам отфильтрованы методикой — включите «Показывать отфильтрованные сигналы»." : "Событий по этим фильтрам нет."}
          </p>
        )}
        <ol>
          {rows.slice(0, 500).map((r, i) => (
            <SignalItem
              key={`${r.code}-${r.signal.code}-${r.signal.index}-${i}`}
              s={r.signal}
              prefix={
                <a href={`/ru/${encodeURIComponent(r.code)}`} onClick={linkHandler(`/ru/${encodeURIComponent(r.code)}`)}>
                  <b>{r.code}</b> {CATEGORY_LABEL[r.category] ?? r.category} ·{" "}
                </a>
              }
            />
          ))}
        </ol>
      </section>
    </main>
  );
}
